import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Instagram, Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Get In Touch
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-[#A3A3A3] max-w-2xl mx-auto">
              Have questions? Want to get involved? We&apos;d love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-8">
                Reach Out
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white mb-1">Email</h3>
                    <a
                      href="mailto:info@3eyeblindrescue.org"
                      className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors"
                    >
                      info@3eyeblindrescue.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white mb-1">Phone</h3>
                    <a
                      href="tel:916-259-3879"
                      className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors"
                    >
                      (916) 259-3879
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white mb-1">Mailing Address</h3>
                    <p className="text-[#A3A3A3]">
                      Three Eye Blind Rescue<br />
                      4010 Foothills Blvd 103-189<br />
                      Roseville, CA 95747
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white mb-1">Instagram</h3>
                    <a
                      href="https://www.instagram.com/3eyeblindrescue/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors"
                    >
                      @3eyeblindrescue
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-12 pt-12 border-t border-[#4A5320]">
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/applications" className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors">
                      Adoption/Foster Application
                    </a>
                  </li>
                  <li>
                    <a href="/applications#volunteer" className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors">
                      Volunteer Application
                    </a>
                  </li>
                  <li>
                    <a href="/donate" className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors">
                      Donation Options
                    </a>
                  </li>
                  <li>
                    <a href="/foster-resources" className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors">
                      Foster Resources
                    </a>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="card-tactical p-8 lg:p-12">
                <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-8">
                  Send a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-mono text-sm uppercase tracking-widest mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name-input"
                      className="form-input"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-mono text-sm uppercase tracking-widest mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="form-input"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-white font-mono text-sm uppercase tracking-widest mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject-input"
                      className="form-input"
                      placeholder="What's this about?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-mono text-sm uppercase tracking-widest mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      data-testid="contact-message-input"
                      className="form-textarea"
                      placeholder="Tell us more..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EIN */}
      <section className="py-12 border-t border-[#4A5320]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#A3A3A3] text-sm font-mono">
            Three Eye Blind Animal Rescue • 501(c)(3) Nonprofit • <span className="text-white">EIN: 99-0978241</span>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
